/**
 * Currency formatting utility for consistent currency display across the application
 * Validates: Requirements 1.6
 */

/**
 * Formats a number as currency with dollar sign, thousand separators, and 2 decimal places
 * 
 * @param {number} amount - The numeric amount to format
 * @returns {string} Formatted currency string (e.g., "$1,234.56")
 * 
 * @example
 * formatCurrency(1234.56) // "$1,234.56"
 * formatCurrency(0) // "$0.00"
 * formatCurrency(-500.5) // "-$500.50"
 * formatCurrency(1000000) // "$1,000,000.00"
 */
export const formatCurrency = (amount) => {
  // Handle null, undefined, or NaN values
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0.00';
  }

  // Convert to number if string
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Handle invalid conversion, Infinity, or -Infinity
  if (isNaN(numAmount) || !isFinite(numAmount)) {
    return '$0.00';
  }

  // Handle negative numbers
  const isNegative = numAmount < 0;
  const absoluteAmount = Math.abs(numAmount);

  // Format with 2 decimal places and thousand separators
  const formatted = absoluteAmount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // Return with appropriate sign and dollar symbol
  // Special case: if the formatted value rounds to 0.00, don't show negative sign
  if (formatted === '0.00') {
    return '$0.00';
  }

  return isNegative ? `-$${formatted}` : `$${formatted}`;
};
